import { ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';

import { Botao } from '@/componentes/Botao';
import { Campo } from '@/componentes/Campo';
import { ListaDeEscolha } from '@/componentes/ListaDeEscolha';
import { formatarAteDuasCasas, formatarData, formatarDataHora, formatarDecimal, formatarInteiro, formatarReais, interpretarDataHora, interpretarNumero, paraCampoDataHora } from '@/lib/formato';
import { HistoriaNaMesa } from '@/historias/mesa';
import { AJUDA_DA_OPCAO_UNICA, opcaoUnica } from '@/lib/escolha-unica';
import { primeiraMaiuscula } from '@/lib/texto';
import { anunciar } from '@/lib/retorno';
import { diaDaOperacao } from '@/lib/dia-da-operacao';
import { sessaoEmMemoria, useSessaoEmMemoria } from '@/modulos/sessao/memoria';

import type { EnvioDeAbastecimento } from './api';
import { LeitorDeCupom, type CupomLido } from './LeitorDeCupom';
import { explicarFalha, useLancarAbastecimento, type CadastrosDoLancamento } from './consultas';
import { consumoDe, estaForaDaFaixa, horasDesde, ladoDoDesvio, textoDaFaixa } from './consumo';
import type { Abastecimento, EquipamentoDoModulo, FaixaConsumo, Ordem, SaldoDeTanque } from './tipos';
import { opcaoDaMaquina } from '@/modulos/equipamentos/opcao-da-maquina';
import { opcaoDaPessoa, useOpcoesComVisualDaFrota } from '@/modulos/equipamentos/opcoesComVisual';
import { litrosEscritos } from './litros';
import { origemDoProdutoDaMaquina, produtoDaMaquina } from './produto-da-maquina';
import { EtiquetaDaUltimaLeitura } from './EtiquetaDaUltimaLeitura';
import { ultimaLeituraComAFila } from './ultima-leitura';
import { AJUDA_DO_PARCEIRO_SEM_POSTO, AJUDA_DO_POSTO_UNICO, AJUDA_DO_TANQUE_UNICO, AJUDA_DO_TIPO_UNICO, tanquesPermitidos, tanqueTravado, tiposPermitidos } from './trava-do-lancamento';

/**
 * Lançar abastecimento — interno ou externo.
 *
 * ## A tela foi otimizada por número de campos, não por beleza
 *
 * O que dá para descontar, foi descontado:
 *
 *  - data e hora são UM campo, não dois, e já vêm com o agora;
 *  - o tanque padrão já vem escolhido — quem abastece no pátio abastece do
 *    mesmo tanque o dia inteiro;
 *  - obra, centro de custo e operador caem sozinhos quando se escolhe o
 *    equipamento, porque o sistema já sabe onde a máquina está;
 *  - o responsável em branco entra como quem está usando o sistema, e quem
 *    decide isso é o banco, não este formulário;
 *  - no abastecimento INTERNO o preço do litro não é campo: ele vem do último
 *    recebimento do tanque. Perguntar preço a quem está com a mangueira na mão
 *    é pedir que ele adivinhe um número que o almoxarifado já tem.
 *
 * O abastecimento EXTERNO tem três campos a mais — posto, preço e comprovante —
 * e eles não são burocracia: são o cupom. Sem comprovante não há como conferir a
 * cobrança do posto depois, e o banco recusa o lançamento.
 *
 * ## O desenho cabe numa tela, e o resto fica atrás de um botão (30/10/2026)
 *
 * O dono, depois de usar: *"o formulário é grande e demanda que o operador
 * tenha que mexer muito no mouse e arrastar muito a página para fazer um
 * lançamento [...] campos como o cupom fiscal você pode colocar dentro de um
 * botão que só aparece se a pessoa selecionar [...] o sistema é usado por
 * pessoas leigas, tem que ser o mais simples e funcional possível"*.
 *
 * Então a tela tem DUAS camadas:
 *
 *  - **o que se digita todo dia**, em duas ou três linhas da grade: tipo,
 *    máquina e data; tanque ou posto, litros e medidor; no externo, produto,
 *    preço e cupom. O consumo é uma linha, não um bloco;
 *  - **o que o sistema já respondeu** — obra, centro de custo, operador e
 *    responsável — aparece como ETIQUETA, numa linha só, e os campos só abrem
 *    em "Ajustar quem e onde". A leitura do cupom pela IA também: um botão, e
 *    a área de enviar a foto só existe depois dele.
 *
 * Nenhum campo saiu e nenhuma regra mudou: o que estava à vista continua a um
 * clique, e o que o sistema preencheu continua escrito na tela, para quem
 * lança conferir sem abrir nada.
 *
 * ## Validação ao sair do campo, cálculo a cada tecla
 *
 * Não é contradição, são duas coisas diferentes. Erro só aparece no `blur`:
 * quem digita "48" a caminho de "4832" não errou nada. O consumo é cálculo, não
 * julgamento — e ele precisa acompanhar a digitação, senão a pessoa termina o
 * campo, sai, e descobre tarde demais que trocou um dígito.
 *
 * ## O que acontece quando o lançamento sai daqui
 *
 * Ele vai para a API, na sessão de quem está logado, e o servidor grava no
 * Postgres o abastecimento e a saída do tanque na mesma transação. Sem sessão
 * válida não há lançamento: a chamada é recusada e leva à entrada. O retorno
 * diz o que entrou, com o código da máquina e os litros.
 */

type NomeDoCampo =
  | 'equipamento'
  | 'quando'
  | 'tanque'
  | 'posto'
  | 'produto'
  | 'litros'
  | 'preco'
  | 'comprovante'
  | 'medidor'
  | 'operador'
  | 'responsavel'
  | 'obra'
  | 'centroCusto';

interface Rascunho {
  readonly tipo: 'interno' | 'externo';
  readonly tanque: string;
  readonly posto: string;
  /** So no EXTERNO. No interno o produto e o do tanque, e nao se escolhe. */
  readonly produto: string;
  readonly ordem: string;
  readonly litros: string;
  readonly preco: string;
  readonly comprovante: string;
  readonly medidor: string;
  readonly operador: string;
  readonly responsavel: string;
  readonly obra: string;
  readonly centroCusto: string;
}

type MapaDeErros = Readonly<Record<NomeDoCampo, string | null>>;
type MapaDePreenchidos = Readonly<Record<NomeDoCampo, boolean>>;

const CAMPOS: readonly NomeDoCampo[] = [
  'equipamento',
  'quando',
  'tanque',
  'posto',
  'produto',
  'litros',
  'preco',
  'comprovante',
  'medidor',
  'operador',
  'responsavel',
  'obra',
  'centroCusto',
];

function mapaVazio<T>(valor: T): Readonly<Record<NomeDoCampo, T>> {
  return Object.fromEntries(CAMPOS.map((campo) => [campo, valor])) as Readonly<Record<NomeDoCampo, T>>;
}

const SEM_ERRO: MapaDeErros = mapaVazio<string | null>(null);
const NADA_PREENCHIDO: MapaDePreenchidos = mapaVazio(false);

/** O rótulo do medidor muda com a máquina: caminhão roda km, escavadeira faz hora. */
function unidadeDoMedidor(tipoMedidor: string | undefined): { readonly rotulo: string; readonly unidade: string } {
  return tipoMedidor === 'odometro'
    ? { rotulo: 'Leitura do odômetro', unidade: 'km' }
    : { rotulo: 'Leitura do horímetro', unidade: 'h' };
}

export function FormularioAbastecimento({
  cadastros,
  ordensAbertas,
  codigoEscolhido,
  aoEscolherEquipamento,
  quando,
  aoMudarQuando,
  equipamento,
  anterior,
  proximo,
  ultimoRegistro,
  faixa,
  titulo = 'Lançar abastecimento',
}: {
  readonly cadastros: CadastrosDoLancamento;
  /** As ordens abertas do equipamento escolhido. Vazio no abastecimento externo. */
  readonly ordensAbertas: readonly Ordem[];
  readonly codigoEscolhido: string;
  readonly aoEscolherEquipamento: (codigo: string) => void;
  /** Valor de `<input type="datetime-local">`. Mora na página, não no rascunho. */
  readonly quando: string;
  readonly aoMudarQuando: (quando: string) => void;
  readonly equipamento: EquipamentoDoModulo | null;
  /** O abastecimento ANTERIOR à data digitada. É dele que sai a base do consumo. */
  readonly anterior: Abastecimento | null;
  /** O primeiro abastecimento POSTERIOR à data digitada, quando existe. */
  readonly proximo: Abastecimento | null;
  /** A maior leitura válida até aquela data. É contra ela que o medidor é validado. */
  readonly ultimoRegistro: number | null;
  readonly faixa: FaixaConsumo | null;
  /** O título do cartão. `null` quando a página já pôs o dela logo acima. */
  readonly titulo?: string | null;
}) {
  const base = useId();
  /* AS DUAS GAVETAS DA TELA (30/10/2026): a leitura do cupom pela IA e os
     campos de quem e onde. Fechadas por padrão; o que está dentro continua
     escrito fora, em etiqueta. */
  const [lendoCupom, setLendoCupom] = useState(false);
  const [ajustandoQuemEOnde, setAjustandoQuemEOnde] = useState(false);
  /* O RASCUNHO GUARDADO (p.16: formulário com mais de dez campos salva
     rascunho automático — "conexão em obra cai, e o preenchimento não pode se
     perder"). Lido UMA vez, na montagem, e guardado em estado: é dele, e não
     de uma segunda leitura do armazenamento, que a página recebe o
     equipamento e a data. */
  const [chaveDoRascunho] = useState(chaveDoRascunhoDaSessao);
  const [recuperado] = useState(() => lerRascunho(chaveDoRascunho, cadastros));
  const [avisoDoRascunho, setAvisoDoRascunho] = useState(recuperado !== null);
  const [rascunho, setRascunho] = useState<Rascunho>(() => recuperado?.rascunho ?? rascunhoInicial(cadastros));
  const [erros, setErros] = useState<MapaDeErros>(SEM_ERRO);
  const [preenchidos, setPreenchidos] = useState<MapaDePreenchidos>(() =>
    recuperado
      ? /* Rascunho recuperado volta SEM marcas: não há como saber o que o
           sistema escreveu e o que a pessoa escreveu. A exceção é a data que
           era a do relógio — ela volta como o agora, e a marca é honesta. */
        { ...NADA_PREENCHIDO, quando: recuperado.quando === null }
      : {
          ...NADA_PREENCHIDO,
          quando: true,
          tanque: true,
          /* O produto nasce com o primeiro abastecível, e a marca é o que torna isso
             honesto: campo respondido pelo sistema sem marca ninguém confere — e
             ninguém confere justamente o que o sistema errou sozinho. Era esse o
             defeito, com o agravante de nem haver campo onde conferir. */
          produto: true,
          responsavel: true,
        },
  );

  /* Equipamento e data moram na PÁGINA (o cartão de contexto usa os dois):
     o rascunho recuperado os devolve para lá depois da montagem. */
  useEffect(() => {
    if (recuperado === null) return;
    aoEscolherEquipamento(recuperado.codigo);
    if (recuperado.quando !== null) aoMudarQuando(recuperado.quando);
  }, [recuperado, aoEscolherEquipamento, aoMudarQuando]);

  /* Guarda a cada mudança, com espera: gravar a cada tecla seria escrever no
     disco trinta vezes por campo. O que ainda não foi gravado quando o
     formulário sai da tela (troca de aba do hub) é gravado na saída. Sem erros
     e sem marcas: só o que a pessoa escreveu. */
  const pendente = useRef<RascunhoGuardado | null | undefined>(undefined);
  useEffect(() => {
    const dados = temConteudo(rascunho, codigoEscolhido)
      ? {
          rascunho,
          codigo: codigoEscolhido,
          quando: preenchidos.quando ? null : quando,
          guardadoEm: new Date().toISOString(),
        }
      : null;
    pendente.current = dados;
    const espera = window.setTimeout(() => {
      guardarRascunho(chaveDoRascunho, dados);
      pendente.current = undefined;
    }, ESPERA_DO_RASCUNHO_MS);
    return () => {
      window.clearTimeout(espera);
    };
  }, [chaveDoRascunho, rascunho, codigoEscolhido, quando, preenchidos.quando]);
  useEffect(
    () => () => {
      if (pendente.current !== undefined) guardarRascunho(chaveDoRascunho, pendente.current);
    },
    [chaveDoRascunho],
  );
  /* A data que o cupom lido pela IA escreveu: a ajuda do campo diz de onde ela veio. */
  const [quandoDoCupom, setQuandoDoCupom] = useState(false);
  /* O que impede o lancamento e NAO e de nenhum campo: um cadastro que falta e
     que esta tela nao cria. Erro de campo mora no campo; este mora no rodape
     do formulario, junto da recusa do servidor. */
  const [impedimento, setImpedimento] = useState<string | null>(null);

  const lancar = useLancarAbastecimento();
  /* A máquina com a foto da frota, o tipo e a obra; a pessoa com as
     iniciais (pedido do dono, 20/10/2026). O código no rótulo, o código
     interno e a placa em etiqueta, o tipo pequeno embaixo (31/10/2026). O
     valor do campo é o CÓDIGO. */
  const comVisualDaFrota = useOpcoesComVisualDaFrota();
  const opcoesDeEquipamento = comVisualDaFrota(cadastros.equipamentos.map((item) => opcaoDaMaquina(item))).map((opcao, i) => ({
    ...opcao,
    valor: cadastros.equipamentos[i]?.codigo ?? opcao.valor,
  }));

  const ehExterno = rascunho.tipo === 'externo';
  const tanqueEscolhido = cadastros.tanques.find((t) => t.id === rascunho.tanque) ?? null;

  const litros = interpretarNumero(rascunho.litros);
  const medidor = interpretarNumero(rascunho.medidor);
  const precoDigitado = interpretarNumero(rascunho.preco);
  const horas = horasDesde(medidor, anterior);
  const consumo = litros === null ? null : consumoDe(litros, horas);
  /* O PRODUTO DESTE LANÇAMENTO. No externo quem escolhe é quem lança; no
     interno é o do tanque, e a chave composta da 0011 garante que seja. */
  const produtoDoLancamento = ehExterno ? rascunho.produto : (tanqueEscolhido?.produtoId ?? '');
  /* A FAIXA JULGA COMBUSTÍVEL, e só — o mesmo critério do banco, onde
     `fora_da_faixa` é nulo fora da categoria `combustivel`. A faixa cadastrada
     é de litro de diesel por hora; medir ARLA por ela acusaria desvio em toda
     máquina que usa ARLA, e aviso que sempre aparece é aviso que ninguém lê. */
  const ehCombustivel =
    cadastros.produtos.find((p) => p.id === produtoDoLancamento)?.categoria === 'combustivel';
  const foraDaFaixa = ehCombustivel && estaForaDaFaixa(consumo, faixa);
  const medida = unidadeDoMedidor(equipamento?.tipoMedidor);

  /* QUEM LANÇA PELOS OUTROS (decisão do dono, 25/10/2026). Pela permissão de
     quem administra a empresa, e não pelo nome do papel: papel novo que a
     receba passa a poder também, sem ninguém mexer aqui. Sem sessão em
     memória a tela não decide — e aí não trava, porque travar por engano é
     impedir o lançamento, que é o pior desfecho desta tela. */
  const sessao = useSessaoEmMemoria();
  const lancaPelosOutros = sessao === null || sessao.permissoes.includes('governanca.gerir');
  /* 0128 · A TRAVA DO PAPEL: os tipos e os tanques que ele libera. O Tipo e o
     Tanque são o campo de opção única (`lib/escolha-unica.ts`): com uma opção
     só, a lista não abre — o valor vem fixo, com o porquê na ajuda, e vai no
     envio. Sem trava, os dois seguem como antes. */
  const tiposDoPapel = tiposPermitidos(sessao);
  const tanquesDoLancamento = tanquesPermitidos(cadastros.tanques, sessao);
  const opcoesDeTipo = tiposDoPapel.map((t) => ({
    valor: t,
    rotulo: t === 'interno' ? 'Interno — tanque ou comboio próprio' : 'Externo — posto',
  }));
  const tipoUnico = opcaoUnica({ opcoes: opcoesDeTipo.map((o) => o.valor), obrigatoria: true }) !== null;
  const tanqueUnico = opcaoUnica({ opcoes: tanquesDoLancamento.map((t) => t.id), obrigatoria: true }) !== null;
  const postoUnico = opcaoUnica({ opcoes: cadastros.postos.map((p) => p.id), obrigatoria: true });
  /* O parceiro cujo fornecedor nao tem posto ligado (31/10/2026): a lista vem
     vazia, e a ajuda do campo diz o que fazer. */
  const semPostoDoParceiro = cadastros.postos.length === 0 && sessao?.parceiro !== undefined;
  /* O nome que o campo travado mostra: o da sessão, e não o do rascunho — o
     rascunho guarda o id, e id não se lê. */
  const nomeDoResponsavelTravado =
    cadastros.responsaveis.find((p) => p.id === rascunho.responsavel)?.nome ?? sessao?.nome ?? 'Você';

  /* O LANÇAMENTO É DE OUTRO DIA? A folha de papel que chega da obra dois dias
     depois, o cupom do posto da semana passada. Nele a obra e o centro de
     custo pré-preenchidos são os de HOJE, e a máquina pode ter sido realocada
     no meio — por isso a tela deixa de afirmar e passa a pedir conferência. */
  const ehRetroativo = (() => {
    const instante = interpretarDataHora(quando);
    if (instante === null) return false;
    /* O dia da OPERACAO, no fuso da operacao — a mesma fonte que o resto do
       sistema usa para dizer "hoje" (src/lib/dia-da-operacao.ts). Comparar
       com o relogio do navegador faria a tela chamar de retroativo o
       lancamento das 23h de quem esta noutro fuso. */
    return diaDaOperacao(instante) !== diaDaOperacao();
  })();

  function mensagemDe(campo: NomeDoCampo, valores: Rascunho, codigo: string): string | null {
    switch (campo) {
      case 'equipamento':
        return codigo ? null : 'Escolha o equipamento que foi abastecido.';
      case 'quando': {
        const instante = interpretarDataHora(quando);
        if (instante === null) return 'Informe a data e a hora do abastecimento.';
        if (instante.getTime() > Date.now()) {
          return 'A data e a hora não podem estar no futuro. Use o momento em que o abastecimento aconteceu.';
        }
        return null;
      }
      case 'tanque':
        if (valores.tipo === 'externo') return null;
        return valores.tanque ? null : 'Escolha de qual tanque saiu o diesel.';
      case 'posto':
        if (valores.tipo === 'interno') return null;
        return valores.posto ? null : 'Escolha em qual posto a máquina abasteceu.';
      case 'produto':
        /* No INTERNO o produto e o do tanque — a chave composta do banco
           (migracao 0011) nao aceita outro, entao nao ha o que escolher. No
           externo nao ha tanque que responda, e antes desta versao o
           formulario ADIVINHAVA: pegava o primeiro produto de categoria
           `combustivel` da lista. Com dois combustiveis cadastrados, todo
           externo ia para o mesmo, sem campo para corrigir. */
        if (valores.tipo === 'interno') return null;
        return valores.produto ? null : 'Escolha o que foi abastecido.';
      case 'comprovante':
        if (valores.tipo === 'interno') return null;
        return valores.comprovante.trim()
          ? null
          : 'Informe o número do cupom ou da nota. É ele que permite conferir a cobrança do posto depois.';
      case 'preco': {
        if (valores.tipo === 'interno') return null;
        const valor = interpretarNumero(valores.preco);
        if (valor === null) return 'Informe o preço do litro que está no cupom.';
        if (valor <= 0) return 'O preço do litro precisa ser maior que zero.';
        return null;
      }
      case 'litros': {
        const valor = interpretarNumero(valores.litros);
        if (valor === null) return 'Informe quantos litros entraram no equipamento.';
        if (valor <= 0) return 'Os litros precisam ser maiores que zero.';
        return null;
      }
      case 'medidor': {
        const valor = interpretarNumero(valores.medidor);
        if (valor === null) {
          return `Informe a leitura ${medida.unidade === 'km' ? 'do odômetro' : 'do horímetro'} no momento do abastecimento.`;
        }
        if (ultimoRegistro !== null && valor < ultimoRegistro) {
          return `A leitura não pode ser menor que o último registro (${formatarAteDuasCasas(ultimoRegistro)} ${medida.unidade}).`;
        }
        /* O TETO existe por causa do lançamento retroativo. Um registro
           intercalado com leitura maior que a do abastecimento seguinte faz o
           consumo daquela linha — que estava certo — sumir da tabela, porque a
           diferença do medidor vira negativa. */
        if (proximo !== null && proximo.horimetro !== null && valor > proximo.horimetro) {
          return `A leitura não pode ser maior que a do abastecimento de ${formatarDataHora(proximo.quando)} (${formatarAteDuasCasas(proximo.horimetro)} ${medida.unidade}). Confira a data e a leitura.`;
        }
        return null;
      }
      /* A OBRA NÃO É OBRIGATÓRIA, e isto é correção de defeito: `obra_id` é
         nula no banco (0008), o servidor a aceita ausente, e a máquina `Não
         alocado` — a que está no pátio esperando frente — não tem obra
         nenhuma. Exigi-la aqui tornava o abastecimento dessa máquina
         impossível de lançar pela mesa, com a pessoa olhando uma lista em que
         nenhuma opção era verdade. */
      case 'obra':
        return null;
      /* Operador, responsável e centro de custo são os campos opcionais da
         tela. Sem eles o lançamento entra com pendência, e é melhor um
         lançamento com pendência visível do que um abastecimento que nunca foi
         registrado porque às 6h da manhã ninguém lembrava quem estava na
         máquina. O responsável em branco entra como quem está usando o
         sistema — é o banco que resolve isso. */
      case 'operador':
      case 'responsavel':
      case 'centroCusto':
        return null;
    }
  }

  function escrever(campo: keyof Rascunho, valor: string) {
    setRascunho((atual) => ({ ...atual, [campo]: valor }));
    if (campo in SEM_ERRO) {
      setPreenchidos((atual) => ({ ...atual, [campo]: false }));
      /* Limpar o erro enquanto se digita não é validar: é parar de apontar um
         problema que a pessoa já está consertando. */
      setErros((atual) => ({ ...atual, [campo]: null }));
    }
  }

  /**
   * O cupom lido pela IA (decisão 14) PREENCHE, e marca: cada campo que a
   * leitura escreveu ganha a marca de preenchido, que some quando a pessoa o
   * toca. O que a leitura não achou fica como estava.
   */
  function aplicarCupom(c: CupomLido) {
    const decimal = (n: number) => String(n).replace('.', ',');
    setRascunho((atual) => ({
      ...atual,
      ...(c.postoId ? { posto: c.postoId } : {}),
      ...(c.produtoId ? { produto: c.produtoId } : {}),
      ...(c.litros !== null ? { litros: decimal(c.litros) } : {}),
      ...(c.precoLitro !== null ? { preco: decimal(c.precoLitro) } : {}),
      ...(c.numero ? { comprovante: c.numero } : {}),
    }));
    setPreenchidos((atual) => ({
      ...atual,
      posto: c.postoId !== null,
      produto: c.produtoId !== null || atual.produto,
      litros: c.litros !== null,
      preco: c.precoLitro !== null,
      comprovante: c.numero !== null,
      quando: c.data !== null || atual.quando,
    }));
    if (c.data) {
      aoMudarQuando(`${c.data}T${c.hora ?? '12:00'}`);
      setQuandoDoCupom(true);
    }
    setErros(SEM_ERRO);
  }

  function conferir(campo: NomeDoCampo) {
    setErros((atual) => ({ ...atual, [campo]: mensagemDe(campo, rascunho, codigoEscolhido) }));
  }

  /** Trocar o tipo troca a origem: tanque e posto nunca convivem. */
  function escolherTipo(tipo: 'interno' | 'externo') {
    setRascunho((atual) => ({
      ...atual,
      tipo,
      tanque: tipo === 'interno' ? tanquePadrao(cadastros) : '',
      /* Um posto só (o parceiro, 0132): ele vem escolhido. */
      posto: tipo === 'externo' ? (opcaoUnica({ opcoes: cadastros.postos.map((p) => p.id), obrigatoria: true }) ?? '') : '',
      /* Voltar ao externo devolve o primeiro abastecível ao campo, e a marca
         de pré-preenchido volta junto. O interno não usa este valor: lá o
         produto é o do tanque. */
      produto:
        tipo === 'externo'
          ? (produtoDaMaquina(
              cadastros.equipamentos.find((e) => e.codigo === codigoEscolhido),
              produtosAbastecveis(cadastros),
              cadastros.tanques,
            ) ?? produtoDeCombustivel(cadastros))
          : '',
      ordem: '',
      preco: '',
      comprovante: '',
    }));
    setErros(SEM_ERRO);
    /* Posto, preco e comprovante acabam de ser apagados: a marca do cupom lido
       (decisao 14) nao pode sobrar num campo vazio. */
    setPreenchidos((atual) => ({ ...atual, tanque: tipo === 'interno', produto: tipo === 'externo', posto: false, preco: false, comprovante: false }));
  }

  /** Escolher o equipamento responde três campos de uma vez. */
  function escolherEquipamento(codigo: string) {
    aoEscolherEquipamento(codigo);

    const escolhido = cadastros.equipamentos.find((item) => item.codigo === codigo) ?? null;
    const obra = escolhido?.obraId ?? '';
    const centroCusto = escolhido?.centroCustoId ?? centroDaObra(cadastros, obra);

    /* O PRODUTO DO POSTO segue a maquina (o mesmo `produtoDaMaquina` do
       celular), enquanto a pessoa nao o escolheu a mao: o pre-preenchido e
       quem marca isso. */
    const doProduto =
      rascunho.tipo === 'externo' && preenchidos.produto
        ? produtoDaMaquina(escolhido, produtosAbastecveis(cadastros), cadastros.tanques)
        : null;
    setRascunho((atual) => ({ ...atual, obra, centroCusto, ordem: '', ...(doProduto ? { produto: doProduto } : {}) }));
    setPreenchidos((atual) => ({
      ...atual,
      equipamento: false,
      obra: obra !== '',
      centroCusto: centroCusto !== '',
    }));
    setErros((atual) => ({ ...atual, equipamento: null, obra: null, centroCusto: null, medidor: null }));
  }

  /** Trocar a obra redesce o centro de custo: ele é consequência dela. */
  function escolherObra(id: string) {
    const centroCusto = centroDaObra(cadastros, id);
    setRascunho((atual) => ({ ...atual, obra: id, centroCusto }));
    setPreenchidos((atual) => ({ ...atual, obra: false, centroCusto: centroCusto !== '' }));
    setErros((atual) => ({ ...atual, obra: null, centroCusto: null }));
  }

  function reiniciar() {
    aoEscolherEquipamento('');
    aoMudarQuando(paraCampoDataHora(new Date()));
    setRascunho(rascunhoInicial(cadastros));
    setPreenchidos({ ...NADA_PREENCHIDO, quando: true, tanque: true, responsavel: true });
    setErros(SEM_ERRO);
    /* Formulário limpo não tem rascunho: o guardado sai junto, na hora. */
    setAvisoDoRascunho(false);
    setLendoCupom(false);
    setAjustandoQuemEOnde(false);
    pendente.current = undefined;
    guardarRascunho(chaveDoRascunho, null);
  }

  function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();

    const conferidos = Object.fromEntries(
      CAMPOS.map((campo) => [campo, mensagemDe(campo, rascunho, codigoEscolhido)]),
    ) as MapaDeErros;
    setErros(conferidos);

    if (Object.values(conferidos).some((mensagem) => mensagem !== null)) return;

    const instante = interpretarDataHora(quando);
    if (instante === null || litros === null || medidor === null || equipamento === null) return;

    const produtoId = produtoDoLancamento;
    if (!produtoId) {
      /* NAO vai para o campo `tanque`: no modo externo esse campo nem e
         renderizado, e a mensagem ficava sem onde aparecer — o botao "Lançar
         abastecimento" simplesmente nao fazia nada, sem uma palavra na tela.
         Este e erro do FORMULARIO (falta um cadastro que nao se resolve aqui),
         e ele aparece onde a recusa do servidor aparece. */
      setImpedimento(
        'Não há produto cadastrado para abastecer. Cadastre o diesel ou o ARLA em Combustível · Cadastros e lance outra vez.',
      );
      return;
    }
    setImpedimento(null);

    const envio: EnvioDeAbastecimento = {
      tipo: rascunho.tipo,
      equipamentoId: equipamento.id,
      produtoId,
      ocorridoEm: instante.toISOString(),
      quantidadeL: litros,
      medidor,
      ...(rascunho.tipo === 'interno' ? { tanqueId: rascunho.tanque } : { postoId: rascunho.posto }),
      ...(rascunho.ordem ? { ordemId: rascunho.ordem } : {}),
      ...(ehExterno && precoDigitado !== null ? { precoLitro: precoDigitado } : {}),
      ...(ehExterno ? { comprovante: rascunho.comprovante.trim() } : {}),
      ...(rascunho.operador ? { operadorId: rascunho.operador } : {}),
      ...(rascunho.responsavel ? { responsavelId: rascunho.responsavel } : {}),
      ...(rascunho.obra ? { obraId: rascunho.obra } : {}),
      ...(rascunho.centroCusto ? { centroCustoId: rascunho.centroCusto } : {}),
      motivo: 'lançamento de abastecimento pela tela',
    };

    /* O consumo fora da faixa NÃO impede o lançamento — ele só precisava estar
       na tela antes de o botão ser clicado, e estava. Máquina em terreno ruim
       consome fora da faixa de verdade, e sistema que recusa o que aconteceu
       ensina a pessoa a lançar número redondo até o sistema aceitar. */
    lancar.mutate(
      { envio },
      {
        onSuccess: (gravado) => {
          const doLancamento = consumo === null ? null : formatarDecimal(consumo, 1);
          /* Retorno de ação é toast (p.30, p.31), com o que entrou — e não
             uma caixa no pé do formulário que ficava depois do próximo
             lançamento começar. */
          const nomeDaMaquina = [equipamento?.tipo, equipamento?.modelo].filter(Boolean).join(' ');
          anunciar({
            tom: foraDaFaixa ? 'ressalva' : 'sucesso',
            titulo: 'Abastecimento lançado',
            alvo: { codigo: gravado.equipamentoCodigo, ...(nomeDaMaquina ? { nome: nomeDaMaquina } : {}) },
            fatos: [
              `${litrosEscritos(gravado.litros)} L${gravado.tanque ? ` do ${gravado.tanque}` : ''}`,
              gravado.horimetro === null ? '' : `${medida.unidade === 'km' ? 'odômetro' : 'horímetro'} ${formatarAteDuasCasas(gravado.horimetro)} ${medida.unidade}`,
              doLancamento === null
                ? ''
                : `${doLancamento} ${medida.unidade === 'km' ? 'L/km' : 'L/h'}${foraDaFaixa ? ', fora da faixa' : ''}`,
            ].filter((f) => f !== ''),
          });
          reiniciar();
        },
      },
    );
  }

  const falha = lancar.isError ? explicarFalha(lancar.error) : null;

  /* O NOME de cada coisa que o sistema respondeu, para a etiqueta — id não se lê. */
  const nomeDaObra = cadastros.obras.find((o) => o.id === rascunho.obra)?.nome ?? null;
  const nomeDoCentro = cadastros.centrosCusto.find((c) => c.id === rascunho.centroCusto)?.nome ?? null;
  const nomeDoOperador = cadastros.operadores.find((p) => p.id === rascunho.operador)?.nome ?? null;
  const nomeDoResponsavel = lancaPelosOutros
    ? (cadastros.responsaveis.find((p) => p.id === rascunho.responsavel)?.nome ?? 'Quem está usando o sistema')
    : nomeDoResponsavelTravado;
  const ordemEscolhida = ordensAbertas.find((o) => o.id === rascunho.ordem) ?? null;
  /* A conferência que a etiqueta pede no retroativo: a obra e o centro vieram
     da alocação de HOJE, e a máquina pode ter sido realocada no meio. */
  const conferirAlocacao = ehRetroativo && !preenchidos.obra && rascunho.obra !== '' && !ajustandoQuemEOnde;
  const equipamentoEscolhido = cadastros.equipamentos.find((e) => e.codigo === codigoEscolhido) ?? null;
  const origemDoProduto =
    ehExterno && preenchidos.produto && rascunho.produto
      ? origemDoProdutoDaMaquina(equipamentoEscolhido, rascunho.produto, produtosAbastecveis(cadastros))
      : null;

  return (
    <form
      onSubmit={enviar}
      noValidate
      /* Grade da R01 (p.28): mede o espaço que tem e escolhe três, duas ou uma
         coluna sozinha. Sem largura mínima — os 640 px de antes faziam o
         formulário rolar de lado em 390 com duas colunas espremidas. */
      className="cartao formulario-grade min-w-0 p-6"
    >
      {titulo === null ? null : <h2 className="campo-linha txt-secao text-tinta">{titulo}</h2>}

      {/* A recusa do servidor no TOPO do formulário (p.22), e não no pé, longe
          de onde a pessoa começa a corrigir. */}
      {impedimento ? (
        <p role="alert" className="campo-linha txt-apoio text-parada">
          {impedimento}
        </p>
      ) : null}

      {falha ? (
        <p role="alert" className="campo-linha txt-apoio text-parada">
          {primeiraMaiuscula(falha.erro)}. {falha.comoResolver}
        </p>
      ) : null}

      {avisoDoRascunho && recuperado ? (
        <div
          role="status"
          className="campo-linha flex flex-wrap items-center justify-between gap-2 rounded-controle border border-borda bg-rebaixada px-4 py-2"
        >
          <span className="txt-apoio text-tinta">
            {recuperado.guardadoEm === null
              ? 'Rascunho recuperado. Confira os campos antes de lançar.'
              : `Rascunho recuperado, de ${formatarDataHora(new Date(recuperado.guardadoEm))}. Confira os campos antes de lançar.`}
          </span>
          <Botao nivel="secundario" tamanho="p" onClick={reiniciar} disabled={lancar.isPending}>
            Descartar rascunho
          </Botao>
        </div>
      ) : null}

      {/* ---- Linha 1: o que, qual e quando ---- */}

      <Campo id={`${base}-tipo`} rotulo="Tipo" {...(tipoUnico ? { ajuda: AJUDA_DO_TIPO_UNICO } : {})}>
        {/* O CAMPO DE OPÇÃO ÚNICA: com um tipo só liberado pelo papel, ele vem
            fixo e o formulário recebe o tipo — o `escolherTipo` roda uma vez,
            e com ele o tanque ou o posto que o tipo pede. */}
        <ListaDeEscolha
          id={`${base}-tipo`}
          rotulo="Tipo"
          valor={rascunho.tipo}
          aoMudar={(valor) => {
            escolherTipo(valor === 'externo' ? 'externo' : 'interno');
          }}
          opcoes={opcoesDeTipo}
        />
      </Campo>

      <Campo id={`${base}-equipamento`} rotulo="Equipamento" erro={erros.equipamento}>
        <ListaDeEscolha
          id={`${base}-equipamento`}
          rotulo="Equipamento"
          valor={codigoEscolhido}
          aoMudar={escolherEquipamento}
          aoSair={() => {
            conferir('equipamento');
          }}
          rotuloVazio="Escolha o equipamento"
          opcoes={opcoesDeEquipamento}
          idDoErro={erros.equipamento ? `${base}-equipamento-erro` : undefined}
          adesivo
        />
      </Campo>

      <Campo
        id={`${base}-quando`}
        rotulo="Data e hora"
        preenchido={preenchidos.quando}
        erro={erros.quando}
        {...(quandoDoCupom
          ? { ajuda: 'Veio do cupom lido pela IA. Confira no papel.' }
          : preenchidos.quando
            ? { ajuda: 'Agora. Troque se foi antes.' }
            : {})}
      >
        <input
          id={`${base}-quando`}
          type="datetime-local"
          value={quando}
          className="controle w-full"
          onChange={(evento) => {
            /* Trocar a data troca o abastecimento anterior, e com ele a base
               do consumo e o piso do medidor: por isso ela sobe para a página
               em vez de ficar no rascunho. */
            aoMudarQuando(evento.target.value);
            setQuandoDoCupom(false);
            /*
             * TROCAR A DATA APAGA A MARCA DE OBRA E CENTRO DE CUSTO, e isso
             * conserta um dado errado que entrava calado.
             *
             * A cascata da máquina preenche obra e centro de custo com a
             * alocação VIGENTE AGORA (`escolherEquipamento`, acima). Para o
             * lançamento de hoje isso está certo. Para o retroativo — a folha
             * de papel que chega da obra dois dias depois, o cupom do posto da
             * semana passada — a máquina pode ter sido realocada no meio, e o
             * custo do diesel ia para a obra ERRADA, com a marca
             * "pré-preenchido" dizendo que o sistema sabia.
             *
             * A tela NÃO tem a alocação histórica para recalcular: quem a tem
             * é o banco (`ativos.alocacao ... periodo @> ocorrido_em`). O que
             * ela pode fazer com honestidade é parar de afirmar: a marca sai,
             * a etiqueta de quem e onde passa a pedir a conferência, e quem
             * lança decide. Melhor uma pergunta do que um número errado em
             * silêncio.
             */
            setPreenchidos((atual) => ({ ...atual, quando: false, obra: false, centroCusto: false }));
            setErros((atual) => ({ ...atual, quando: null, medidor: null }));
          }}
          onBlur={() => {
            conferir('quando');
          }}
          {...descrever(`${base}-quando`, erros.quando)}
        />
      </Campo>

      {/* ---- Linha 2 (e 3, no externo): de onde, quanto e o medidor ---- */}

      {ehExterno ? (
        <>
          {/* 0132 · O POSTO NO CAMPO DE OPÇÃO ÚNICA (decisão 58): o posto parceiro
              só recebe os postos do fornecedor dele (0123), e com um só o campo
              vem fixo e preenchido, como o tipo e o tanque. Na empresa com
              vários postos, continua a lista. */}
          <Campo
            id={`${base}-posto`}
            rotulo="Posto"
            preenchido={preenchidos.posto}
            erro={erros.posto}
            {...(postoUnico !== null ? { ajuda: AJUDA_DO_POSTO_UNICO } : semPostoDoParceiro ? { ajuda: AJUDA_DO_PARCEIRO_SEM_POSTO } : {})}
          >
            <ListaDeEscolha
              id={`${base}-posto`}
              rotulo="Posto"
              valor={rascunho.posto}
              aoMudar={(valor) => {
                escrever('posto', valor);
              }}
              aoSair={() => {
                conferir('posto');
              }}
              rotuloVazio="Escolha o posto"
              opcoes={cadastros.postos.map((posto) => ({
                valor: posto.id,
                rotulo: `${posto.nome}${posto.municipio ? ` · ${posto.municipio}` : ''}`,
              }))}
              idDoErro={erros.posto ? `${base}-posto-erro` : undefined}
            />
          </Campo>

          {/* O QUE foi abastecido, e não o que o formulário adivinhou. Vem da
              FICHA da máquina (diesel, gasolina…) quando ela diz; a ajuda
              diz de onde veio, para quem lança conferir (pedido do dono,
              30/10/2026). No interno este campo não existe: o produto é o do
              tanque, e a chave composta do banco (migração 0011) não aceita
              outro. */}
          <Campo
            id={`${base}-produto`}
            rotulo="Produto"
            preenchido={preenchidos.produto}
            erro={erros.produto}
            ajuda={
              origemDoProduto === 'ficha'
                ? `Da ficha de ${codigoEscolhido}: ${combustivelEscrito(equipamentoEscolhido?.combustivel)}.`
                : origemDoProduto === 'historico'
                  ? `Do último abastecimento de ${codigoEscolhido}.`
                  : 'Diesel, ARLA ou outro combustível. Lubrificante tem tela própria.'
            }
          >
            <select
              id={`${base}-produto`}
              value={rascunho.produto}
              className="controle w-full"
              onChange={(evento) => {
                escrever('produto', evento.target.value);
              }}
              onBlur={() => {
                conferir('produto');
              }}
              {...descrever(`${base}-produto`, erros.produto)}
            >
              <option value="">Escolha o produto</option>
              {produtosAbastecveis(cadastros).map((produto) => (
                <option key={produto.id} value={produto.id}>
                  {produto.nome}
                </option>
              ))}
            </select>
          </Campo>
        </>
      ) : (
        <Campo
          id={`${base}-tanque`}
          rotulo="Tanque"
          preenchido={preenchidos.tanque}
          erro={erros.tanque}
          ajuda={
            tanqueUnico
              ? `${tanqueTravado(sessao) ? AJUDA_DO_TANQUE_UNICO : AJUDA_DA_OPCAO_UNICA} ${ajudaDoTanque(tanqueEscolhido)}`
              : ajudaDoTanque(tanqueEscolhido)
          }
        >
          {/* O RÓTULO DIZ O PRODUTO E O VOLUME, e não só o nome. Quem escolhe
              o tanque escolhe por aquilo: com dois tanques no pátio, um de
              diesel e um de ARLA, "Tanque 2" não responde qual é qual — e o
              volume é o que decide se cabe a saída que a pessoa vai lançar.
              O CAMPO DE OPÇÃO ÚNICA: com um tanque só (a trava do papel, ou a
              empresa com um tanque), ele vem fixo, preenchido. */}
          <ListaDeEscolha
            id={`${base}-tanque`}
            rotulo="Tanque"
            valor={rascunho.tanque}
            aoMudar={(valor) => {
              escrever('tanque', valor);
            }}
            aoSair={() => {
              conferir('tanque');
            }}
            rotuloVazio="Escolha o tanque"
            opcoes={tanquesDoLancamento.map((tanque) => ({
              valor: tanque.id,
              rotulo: tanque.nome,
              /* O produto em etiqueta neutra (31/10/2026): é o que separa o
                 tanque de diesel do de ARLA, e a pessoa o confere de olho. */
              etiquetas: [{ texto: tanque.produto, tom: 'neutro', titulo: 'Produto' }],
              detalhe: `saldo ${formatarInteiro(tanque.saldoCalculadoL)} L`,
            }))}
            idDoErro={erros.tanque ? `${base}-tanque-erro` : undefined}
          />
        </Campo>
      )}

      <HistoriaNaMesa codigo="CMP-CMB-02" className="min-w-0">
        <Campo id={`${base}-litros`} rotulo="Litros" preenchido={preenchidos.litros} erro={erros.litros}>
          <div className="flex items-center gap-2">
            <input
              id={`${base}-litros`}
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={rascunho.litros}
              placeholder="180"
              className="controle w-full"
              onChange={(evento) => {
                escrever('litros', evento.target.value);
              }}
              onBlur={() => {
                conferir('litros');
              }}
              {...descrever(`${base}-litros`, erros.litros)}
            />
            <span className="txt-corpo shrink-0 text-tinta-apagada">L</span>
          </div>
        </Campo>
      </HistoriaNaMesa>

      {ehExterno ? (
        <>
          <Campo id={`${base}-preco`} rotulo="Preço do litro" preenchido={preenchidos.preco} erro={erros.preco}>
            <div className="flex items-center gap-2">
              <input
                id={`${base}-preco`}
                type="text"
                inputMode="decimal"
                autoComplete="off"
                value={rascunho.preco}
                placeholder="6,40"
                className="controle w-full"
                onChange={(evento) => {
                  escrever('preco', evento.target.value);
                }}
                onBlur={() => {
                  conferir('preco');
                }}
                {...descrever(`${base}-preco`, erros.preco)}
              />
              <span className="txt-corpo shrink-0 text-tinta-apagada">/L</span>
            </div>
          </Campo>

          <Campo
            id={`${base}-comprovante`}
            rotulo="Comprovante"
            preenchido={preenchidos.comprovante}
            erro={erros.comprovante}
            ajuda="Número do cupom ou da nota, como está impresso."
          >
            <input
              id={`${base}-comprovante`}
              type="text"
              autoComplete="off"
              value={rascunho.comprovante}
              placeholder="Cupom 55321"
              className="controle w-full"
              onChange={(evento) => {
                escrever('comprovante', evento.target.value);
              }}
              onBlur={() => {
                conferir('comprovante');
              }}
              {...descrever(`${base}-comprovante`, erros.comprovante)}
            />
          </Campo>
        </>
      ) : null}

      <HistoriaNaMesa codigo="CMP-CMB-03" className="min-w-0">
        {/* A ULTIMA LEITURA AO LADO DO CAMPO (decisao 54): a etiqueta diz o
            ultimo horimetro ou km registrado da maquina. A ajuda debaixo so
            aparece quando o piso desta DATA e outro — o lancamento de um dia
            que ja passou e conferido contra a leitura daquele dia. */}
        <Campo
          id={`${base}-medidor`}
          rotulo={medida.rotulo}
          erro={erros.medidor}
          {...(equipamento
            ? { etiqueta: <EtiquetaDaUltimaLeitura leitura={ultimaLeituraComAFila(equipamento)} unidade={medida.unidade === 'km' ? 'km' : 'h'} /> }
            : {})}
          {...(equipamento === null
            ? { ajuda: 'Escolha o equipamento para ver a última leitura.' }
            : ultimoRegistro !== null && ultimoRegistro !== equipamento.ultimaLeitura
              ? { ajuda: `Para esta data, o último registro é ${formatarAteDuasCasas(ultimoRegistro)} ${medida.unidade}.` }
              : {})}
        >
          <div className="flex items-center gap-2">
            <input
              id={`${base}-medidor`}
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={rascunho.medidor}
              placeholder={ultimoRegistro === null ? '4.832' : formatarAteDuasCasas(ultimoRegistro)}
              className="controle w-full"
              onChange={(evento) => {
                escrever('medidor', evento.target.value);
              }}
              onBlur={() => {
                conferir('medidor');
              }}
              {...descrever(`${base}-medidor`, erros.medidor)}
            />
            <span className="txt-corpo shrink-0 text-tinta-apagada">{medida.unidade}</span>
          </div>
        </Campo>
      </HistoriaNaMesa>

      {/* ---- O consumo, numa linha ---- */}

      <div className="campo-linha min-w-0">
        <LinhaDeConsumo
          codigo={codigoEscolhido}
          tipo={equipamento?.tipo ?? null}
          anterior={anterior}
          litros={litros}
          horas={horas}
          consumo={consumo}
          faixa={faixa}
          foraDaFaixa={foraDaFaixa}
          unidade={medida.unidade}
        />
      </div>

      {/* ---- Quem e onde: o que o sistema respondeu, em etiqueta; os campos
              atrás do botão. E a leitura do cupom pela IA, atrás do dela. ---- */}

      <div className="campo-linha flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
        <span className="txt-rotulo-campo shrink-0">Quem e onde</span>
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          <Etiqueta rotulo="Obra" valor={nomeDaObra ?? 'Sem obra'} vazia={nomeDaObra === null} marcada={preenchidos.obra} />
          <Etiqueta
            rotulo="Centro de custo"
            valor={nomeDoCentro ?? 'Sem centro'}
            vazia={nomeDoCentro === null}
            marcada={preenchidos.centroCusto}
          />
          <Etiqueta rotulo="Operador" valor={nomeDoOperador ?? 'Sem operador'} vazia={nomeDoOperador === null} />
          <Etiqueta rotulo="Responsável" valor={nomeDoResponsavel} marcada={lancaPelosOutros && preenchidos.responsavel} />
          {!ehExterno && ordensAbertas.length > 0 ? (
            <Etiqueta
              rotulo="Ordem"
              valor={ordemEscolhida ? `${formatarInteiro(ordemEscolhida.numero)}` : `${formatarInteiro(ordensAbertas.length)} aberta${ordensAbertas.length > 1 ? 's' : ''}`}
              vazia={ordemEscolhida === null}
            />
          ) : null}
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {ehExterno ? (
            <Botao
              nivel="fantasma"
              tamanho="p"
              aria-expanded={lendoCupom}
              aria-controls={`${base}-leitor-de-cupom`}
              onClick={() => {
                setLendoCupom((aberto) => !aberto);
              }}
            >
              <Sparkles aria-hidden="true" className="size-[var(--icone-controle)]" />
              {lendoCupom ? 'Fechar o cupom' : 'Ler cupom pela IA'}
            </Botao>
          ) : null}
          <Botao
            nivel="fantasma"
            tamanho="p"
            aria-expanded={ajustandoQuemEOnde}
            aria-controls={`${base}-quem-e-onde`}
            onClick={() => {
              setAjustandoQuemEOnde((aberto) => !aberto);
            }}
          >
            {ajustandoQuemEOnde ? (
              <ChevronUp aria-hidden="true" className="size-[var(--icone-controle)]" />
            ) : (
              <ChevronDown aria-hidden="true" className="size-[var(--icone-controle)]" />
            )}
            {ajustandoQuemEOnde ? 'Fechar quem e onde' : 'Ajustar quem e onde'}
          </Botao>
        </div>
        {conferirAlocacao ? (
          <span className="txt-ajuda w-full text-atencao-texto">
            A obra e o centro de custo são os da alocação de hoje. Para um abastecimento de outro dia, confira se eram estes.
          </span>
        ) : null}
      </div>

      {ehExterno && lendoCupom ? (
        <div id={`${base}-leitor-de-cupom`} className="campo-linha min-w-0">
          <LeitorDeCupom aoLer={aplicarCupom} />
        </div>
      ) : null}

      {ajustandoQuemEOnde ? (
        /* A GAVETA ABERTA e a mesma grade do formulario, em bloco rebaixado:
           os campos entram nas colunas da tela, sem segunda grade a medir. */
        <div id={`${base}-quem-e-onde`} className="campo-linha formulario-grade bloco-rebaixado p-4">
          <Campo
            id={`${base}-operador`}
            rotulo="Operador"
            opcional
            erro={erros.operador}
            ajuda="Sem operador, o lançamento entra na lista com pendência."
          >
            <ListaDeEscolha
              id={`${base}-operador`}
              rotulo="Operador"
              valor={rascunho.operador}
              aoMudar={(valor) => {
                escrever('operador', valor);
              }}
              rotuloVazio="Sem operador"
              opcoes={cadastros.operadores.map((pessoa) => opcaoDaPessoa(pessoa))}
              idDoErro={erros.operador ? `${base}-operador-erro` : undefined}
            />
          </Campo>

          {/*
           * RESPONSÁVEL É TRAVADO EM QUEM ESTÁ LANÇANDO — decisão do dono,
           * 25/10/2026: *"Responsável já vem travado o próprio usuário, a não ser
           * que for administrador"*.
           *
           * Responsável é QUEM LIBEROU O DIESEL. É a assinatura do lançamento, e
           * assinatura que qualquer um troca por outro nome não é assinatura: o
           * campo aberto deixava registrar a saída de 300 L em nome de um colega,
           * sem nada acusando. Quem está na frente do sistema é quem responde.
           *
           * O destravamento é por PERMISSÃO, e não pelo nome do papel: quem
           * administra a empresa (`governanca.gerir`) lança pelos outros — é ele
           * que digita a folha de papel que chegou da obra dois dias depois, e
           * nela o responsável é outra pessoa. Um papel novo que receba essa
           * permissão passa a poder também, sem ninguém mexer aqui.
           */}
          <Campo
            id={`${base}-responsavel`}
            rotulo="Responsável"
            preenchido={preenchidos.responsavel}
            erro={erros.responsavel}
            ajuda={
              lancaPelosOutros
                ? 'Quem liberou o diesel. Vem com quem está no sistema; troque quando estiver lançando por outra pessoa.'
                : 'Quem liberou o diesel. É você, porque é você quem está lançando — quem administra a empresa pode lançar em nome de outra pessoa.'
            }
          >
            {lancaPelosOutros ? (
              <ListaDeEscolha
                id={`${base}-responsavel`}
                rotulo="Responsável"
                valor={rascunho.responsavel}
                aoMudar={(valor) => {
                  escrever('responsavel', valor);
                }}
                rotuloVazio="Quem está usando o sistema"
                opcoes={cadastros.responsaveis.map((pessoa) => opcaoDaPessoa(pessoa))}
                idDoErro={erros.responsavel ? `${base}-responsavel-erro` : undefined}
              />
            ) : (
              /* Travado, e MOSTRADO — não escondido. Um campo que some leva junto
                 a informação de quem vai responder pelo lançamento; um campo que
                 fica, desabilitado e com o nome escrito, diz quem é. */
              <input
                id={`${base}-responsavel`}
                type="text"
                readOnly
                disabled
                value={nomeDoResponsavelTravado}
                className="controle w-full"
              />
            )}
          </Campo>

          <Campo
            id={`${base}-obra`}
            rotulo="Obra"
            opcional
            preenchido={preenchidos.obra}
            erro={erros.obra}
            /* A ajuda fala do estado DE AGORA, e não de um estado possível: com a
               obra escolhida à mão, dizer "Sem obra…" contradiz o campo que está
               logo abaixo. Cada frase tem a sua condição. */
            ajuda={
              preenchidos.obra && codigoEscolhido
                ? `Veio da alocação de ${codigoEscolhido}.`
                : ehRetroativo && rascunho.obra !== ''
                  ? 'A obra veio da alocação de hoje. Para um abastecimento de outro dia, confira se era esta.'
                  : rascunho.obra === ''
                    ? 'Sem obra, o diesel não é apropriado a nenhum centro de custo e o lançamento entra na lista com pendência.'
                    : ''
            }
          >
            <select
              id={`${base}-obra`}
              value={rascunho.obra}
              className="controle w-full"
              onChange={(evento) => {
                escolherObra(evento.target.value);
              }}
              onBlur={() => {
                conferir('obra');
              }}
              {...descrever(`${base}-obra`, erros.obra)}
            >
              <option value="">Sem obra</option>
              {cadastros.obras.map((obra) => (
                <option key={obra.id} value={obra.id}>
                  {obra.nome}
                </option>
              ))}
            </select>
          </Campo>

          <Campo
            id={`${base}-centro`}
            rotulo="Centro de custo"
            opcional
            preenchido={preenchidos.centroCusto}
            erro={erros.centroCusto}
            ajuda={
              preenchidos.centroCusto
                ? 'Veio da obra escolhida.'
                : ehRetroativo && rascunho.centroCusto !== ''
                  ? 'Veio da alocação de hoje. Confira se era este na data do abastecimento.'
                  : rascunho.centroCusto === ''
                    ? 'Sem centro de custo, o lançamento entra na lista com pendência.'
                    : ''
            }
          >
            <select
              id={`${base}-centro`}
              value={rascunho.centroCusto}
              className="controle w-full"
              onChange={(evento) => {
                escrever('centroCusto', evento.target.value);
              }}
            >
              <option value="">Sem centro de custo</option>
              {cadastros.centrosCusto.map((centro) => (
                <option key={centro.id} value={centro.id}>
                  {centro.nome}
                </option>
              ))}
            </select>
          </Campo>

          {!ehExterno && ordensAbertas.length > 0 ? (
            <Campo
              id={`${base}-ordem`}
              rotulo="Ordem de abastecimento"
              opcional
              ajuda="A ordem é a autorização. Ligar o lançamento a ela é o que mostra autorizado e entregue lado a lado."
            >
              <select
                id={`${base}-ordem`}
                value={rascunho.ordem}
                className="controle w-full"
                onChange={(evento) => {
                  escrever('ordem', evento.target.value);
                }}
              >
                <option value="">Sem ordem</option>
                {ordensAbertas.map((ordem) => (
                  <option key={ordem.id} value={ordem.id}>
                    Ordem {formatarInteiro(ordem.numero)} · saldo de {formatarInteiro(ordem.saldoL)} L
                  </option>
                ))}
              </select>
            </Campo>
          ) : null}
        </div>
      ) : null}

      {/* Secundário à esquerda, primário à direita (p.15). E o primário desta
          tela é o ÚNICO botão laranja dela: lançar é a ação que a tela existe
          para provocar. */}
      {/* `flex-wrap`: em 390 os dois rótulos não cabem lado a lado, e sem
          quebra "Lançar abastecimento" saía cortado pela borda do cartão. */}
      <div className="campo-linha barra-de-acoes flex-wrap">
        <Botao nivel="secundario" onClick={reiniciar} disabled={lancar.isPending}>
          Limpar formulário
        </Botao>
        <Botao nivel="primario" type="submit" disabled={lancar.isPending}>
          {lancar.isPending ? 'Lançando…' : 'Lançar abastecimento'}
        </Botao>
      </div>
    </form>
  );
}

/**
 * A ETIQUETA DE QUEM E ONDE (p.25: atributo, neutra, raio 8): o nome do que o
 * sistema respondeu, com o rótulo em cinza na frente. A marcada pelo sistema
 * ganha "· pré-preenchido", como o campo ganharia; a vazia fica no apagado,
 * para "Sem obra" não parecer uma obra chamada assim.
 */
function Etiqueta({
  rotulo,
  valor,
  vazia = false,
  marcada = false,
}: {
  readonly rotulo: string;
  readonly valor: string;
  readonly vazia?: boolean;
  readonly marcada?: boolean;
}) {
  return (
    <span className="inline-flex min-h-[var(--selo-altura)] max-w-full items-center gap-1 rounded-etiqueta bg-rebaixada px-2 py-0.5 txt-selo">
      <span className="shrink-0 text-tinta-secundaria">{rotulo}</span>
      <span className={`truncate ${vazia ? 'text-tinta-apagada' : 'text-tinta'}`}>{valor}</span>
      {marcada ? <span className="shrink-0 text-tinta-apagada">· pré-preenchido</span> : null}
    </span>
  );
}

/** O combustível da ficha como a tela o escreve: "diesel", "gasolina"… */
function combustivelEscrito(valor: string | null | undefined): string {
  switch (valor) {
    case 'diesel':
      return 'diesel';
    case 'gasolina':
      return 'gasolina';
    case 'etanol':
      return 'etanol';
    case 'flex':
      return 'flex';
    case 'gnv':
      return 'GNV';
    case 'eletrico':
      return 'elétrico';
    default:
      return 'não informado';
  }
}

/* ==========================================================================
 * As funções pequenas que o formulário usa, e nada mais
 * ======================================================================= */

function tanquePadrao(cadastros: CadastrosDoLancamento): string {
  /* 0128 · o padrão da empresa só vale se o papel o libera: com a trava, o
     primeiro liberado é a origem — senão o envio levaria um tanque que o campo
     nem mostra, e o servidor recusaria. */
  const tanques = tanquesPermitidos(cadastros.tanques, sessaoEmMemoria());
  return tanques.find((t) => t.padrao)?.id ?? tanques[0]?.id ?? '';
}

function centroDaObra(cadastros: CadastrosDoLancamento, obraId: string): string {
  return cadastros.obras.find((o) => o.id === obraId)?.centroCustoId ?? '';
}

/**
 * O que se pode pôr num equipamento pela tela de abastecimento.
 *
 * Combustível e ARLA. Lubrificante NÃO: ele se aplica em ponto (motor,
 * hidráulico, transmissão), pode ser medido em quilo e tem tela própria —
 * misturá-lo aqui faria a coluna de litros somar graxa com diesel, que é
 * exatamente o que a tabela separada existe para evitar.
 */
function produtosAbastecveis(cadastros: CadastrosDoLancamento) {
  return cadastros.produtos.filter((p) => p.categoria === 'combustivel' || p.categoria === 'arla');
}

/**
 * O primeiro abastecível, só como VALOR INICIAL do campo.
 *
 * Antes esta função decidia o lançamento inteiro: o externo gravava o que ela
 * devolvesse, sem campo na tela. Com dois combustíveis cadastrados — e a tela
 * de cadastro agora torna isso fácil — todo externo era atribuído ao mesmo, e
 * ARLA externo era irregistrável. Agora ela só preenche; quem decide é quem
 * lança, e o campo nasce marcado como pré-preenchido.
 */
function produtoDeCombustivel(cadastros: CadastrosDoLancamento): string {
  return produtosAbastecveis(cadastros)[0]?.id ?? '';
}

function rascunhoInicial(cadastros: CadastrosDoLancamento): Rascunho {
  /* Quem está preenchendo já vem respondido no campo Responsável, e o campo
     nasce marcado como pré-preenchido — campo respondido sem marca ninguém
     confere. O nome sai da sessão de verdade, e o casamento é pelo id da
     pessoa, não pelo nome: dois "João Silva" na mesma empresa deixariam o
     formulário escolher o errado em silêncio. */
  const eu = sessaoEmMemoria()?.usuarioId ?? null;
  return {
    tipo: 'interno',
    tanque: tanquePadrao(cadastros),
    posto: '',
    produto: produtoDeCombustivel(cadastros),
    ordem: '',
    litros: '',
    preco: '',
    comprovante: '',
    medidor: '',
    /* O OPERADOR JÁ VEM RESPONDIDO quando quem está lançando é um operador —
       no comboio e no celular é o caso normal: quem abastece lança. Vem
       respondido e NÃO travado, ao contrário do responsável: o almoxarife
       lança pelo operador que estava na máquina, e esse nome é dado da
       operação, não assinatura de quem digitou. Quem não é operador (o
       almoxarife, a controladoria) não está na lista e o campo nasce vazio. */
    operador: eu !== null && cadastros.operadores.some((p) => p.id === eu) ? eu : '',
    responsavel: eu !== null && cadastros.responsaveis.some((p) => p.id === eu) ? eu : '',
    obra: '',
    centroCusto: '',
  };
}

/* ==========================================================================
 * O rascunho guardado no navegador (p.16)
 * ======================================================================= */

interface RascunhoGuardado {
  readonly rascunho: Rascunho;
  readonly codigo: string;
  /** `null` quando a data era a do relógio do sistema: ela volta como o agora. */
  readonly quando: string | null;
  /** ISO. `null` quando o que estava guardado não era uma data. */
  readonly guardadoEm: string | null;
}

const ESPERA_DO_RASCUNHO_MS = 500;

/**
 * Um rascunho por pessoa: no computador do almoxarifado, que é de todo mundo,
 * o formulário pela metade de um não aparece para o outro.
 */
function chaveDoRascunhoDaSessao(): string {
  return `ct-frotas:combustivel:rascunho-do-abastecimento:${sessaoEmMemoria()?.usuarioId ?? 'sem-sessao'}`;
}

/** Só vale guardar o que a pessoa escreveu — o formulário recém-aberto não é rascunho. */
function temConteudo(r: Rascunho, codigo: string): boolean {
  return (
    codigo !== '' ||
    r.tipo === 'externo' ||
    [r.posto, r.litros, r.preco, r.comprovante, r.medidor, r.operador, r.obra, r.centroCusto].some(
      (valor) => valor.trim() !== '',
    )
  );
}

/**
 * O armazenamento pode não existir (janela privada, cota cheia, bloqueio do
 * navegador). Sem ele o formulário funciona igual, só sem rascunho.
 */
function guardarRascunho(chave: string, dados: RascunhoGuardado | null): void {
  try {
    if (dados === null) window.localStorage.removeItem(chave);
    else window.localStorage.setItem(chave, JSON.stringify(dados));
  } catch {
    /* sem armazenamento: segue sem rascunho */
  }
}

/**
 * O rascunho guardado, conferido contra os cadastros de HOJE.
 *
 * O que foi escolhido numa lista só volta se ainda estiver na lista: um tanque
 * arquivado desde ontem voltaria como valor que o `select` não mostra e que o
 * banco recusa, sem a pessoa ver por quê. A ordem de abastecimento não volta —
 * ela pode ter sido atendida ou cancelada no meio do caminho.
 */
function lerRascunho(chave: string, cadastros: CadastrosDoLancamento): RascunhoGuardado | null {
  let cru: unknown;
  try {
    const guardado = window.localStorage.getItem(chave);
    if (guardado === null) return null;
    cru = JSON.parse(guardado);
  } catch {
    return null;
  }
  if (typeof cru !== 'object' || cru === null) return null;
  const dados = cru as Record<string, unknown>;
  const r = (typeof dados['rascunho'] === 'object' && dados['rascunho'] !== null ? dados['rascunho'] : {}) as Record<
    string,
    unknown
  >;
  const texto = (valor: unknown) => (typeof valor === 'string' ? valor : '');
  const naLista = (valor: unknown, lista: readonly { readonly id: string }[]) =>
    lista.some((item) => item.id === valor) ? texto(valor) : '';

  const inicial = rascunhoInicial(cadastros);
  /* 0128 · o rascunho guardado antes da trava do papel volta no tipo que o
     papel libera, e o tanque, entre os liberados. */
  const tipos = tiposPermitidos(sessaoEmMemoria());
  const tipo = tipos.length === 1 ? (tipos[0] ?? 'interno') : r['tipo'] === 'externo' ? 'externo' : 'interno';
  const codigo = cadastros.equipamentos.some((e) => e.codigo === dados['codigo']) ? texto(dados['codigo']) : '';
  const quando = texto(dados['quando']);
  const guardadoEm = texto(dados['guardadoEm']);

  return {
    rascunho: {
      tipo,
      tanque: tipo === 'interno' ? naLista(r['tanque'], tanquesPermitidos(cadastros.tanques, sessaoEmMemoria())) || inicial.tanque : '',
      posto: tipo === 'externo' ? naLista(r['posto'], cadastros.postos) : '',
      produto: tipo === 'externo' ? naLista(r['produto'], produtosAbastecveis(cadastros)) || inicial.produto : '',
      ordem: '',
      litros: texto(r['litros']),
      preco: tipo === 'externo' ? texto(r['preco']) : '',
      comprovante: tipo === 'externo' ? texto(r['comprovante']) : '',
      medidor: texto(r['medidor']),
      operador: naLista(r['operador'], cadastros.operadores),
      responsavel: naLista(r['responsavel'], cadastros.responsaveis) || inicial.responsavel,
      obra: naLista(r['obra'], cadastros.obras),
      centroCusto: naLista(r['centroCusto'], cadastros.centrosCusto),
    },
    codigo,
    quando: interpretarDataHora(quando) === null ? null : quando,
    guardadoEm: Number.isNaN(Date.parse(guardadoEm)) ? null : guardadoEm,
  };
}

/** Numa linha (30/10/2026): o volume e o preço, que é o que decide a escolha. */
function ajudaDoTanque(tanque: SaldoDeTanque | null): string {
  if (tanque === null) return 'O preço do litro vem do tanque, não deste formulário.';
  if (tanque.precoLitro === null) {
    return `${formatarInteiro(tanque.saldoCalculadoL)} L no tanque. Sem nota recebida: o lançamento entra sem preço.`;
  }
  return `${formatarInteiro(tanque.saldoCalculadoL)} L no tanque · litro a ${formatarReais(tanque.precoLitro)}, da última compra.`;
}


/** `aria-invalid` + `aria-describedby` apontando para o erro que `Campo` desenha. */
function descrever(id: string, erro: string | null | undefined) {
  return erro ? { 'aria-invalid': true, 'aria-describedby': `${id}-erro` } : {};
}

/**
 * O consumo que o lançamento implica, a cada tecla — numa LINHA, e não num
 * bloco (30/10/2026): o número, a conta e o aviso cabem numa faixa de 40 px,
 * e a tela deixa de rolar por causa dele.
 *
 * É o único jeito de o erro de digitação ser pego por quem sabe. Um zero a mais
 * nos litros ou um dígito trocado no horímetro passa despercebido no campo — o
 * número parece plausível —, mas vira um consumo que quem trabalha na obra olha
 * e diz "isso aí não existe".
 *
 * O aviso de faixa NÃO BLOQUEIA e não é erro: é informação chegando antes da
 * decisão, que é o único momento em que ela ainda serve para alguma coisa.
 */
function LinhaDeConsumo({
  codigo,
  tipo,
  anterior,
  litros,
  horas,
  consumo,
  faixa,
  foraDaFaixa,
  unidade,
}: {
  readonly codigo: string;
  readonly tipo: string | null;
  readonly anterior: Abastecimento | null;
  readonly litros: number | null;
  readonly horas: number | null;
  readonly consumo: number | null;
  readonly faixa: FaixaConsumo | null;
  readonly foraDaFaixa: boolean;
  readonly unidade: string;
}) {
  const porUnidade = unidade === 'km' ? 'L/km' : 'L/h';
  const semConta = consumo === null || horas === null || litros === null || anterior === null;

  /* Fundo de ATENÇÃO (âmbar, p.12) quando fora da faixa: é alerta, e laranja é
     só marca e ação — no laranja ele competiria com o botão primário.

     SEM `role="status"`. O texto muda a cada tecla digitada em Litros e no
     medidor — a decisão declarada da tela é "cálculo a cada tecla" —, e
     região viva relida a cada dígito impede quem depende de leitor de tela
     de terminar o campo. O julgamento continua no blur, e lá o erro do
     campo já é `role="alert"`. */
  return (
    <div
      className={`flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 rounded-controle px-3 py-2 ${
        foraDaFaixa && !semConta ? 'bg-atencao-fundo' : 'bloco-rebaixado'
      }`}
    >
      <span className="txt-rotulo shrink-0 text-tinta-secundaria">Consumo</span>
      {semConta ? (
        <span className="txt-apoio text-tinta-secundaria">{explicarFaltaDeConsumo(codigo, anterior, litros, horas, unidade)}</span>
      ) : (
        <>
          <span className={`txt-corpo font-semibold tabular-nums ${foraDaFaixa ? 'text-atencao-texto' : 'text-tinta'}`}>
            {formatarDecimal(consumo, 1)} <span className="font-normal text-tinta-secundaria">{porUnidade}</span>
          </span>
          <span className="txt-apoio text-tinta-secundaria">
            {formatarInteiro(litros)} L em {formatarInteiro(horas)} {unidade} desde {formatarData(anterior.quando)}
            {anterior.horimetro === null ? '' : ` (${formatarAteDuasCasas(anterior.horimetro)} ${unidade})`}.
          </span>
          {foraDaFaixa && faixa !== null ? (
            <span className="txt-apoio text-atencao-texto">
              {ladoDoDesvio(consumo, faixa) === 'acima' ? 'Acima' : 'Abaixo'} da faixa de {tipo ?? 'este tipo de máquina'} (
              {textoDaFaixa(faixa.minimo, faixa.maximo, porUnidade)}). Confira os litros e a leitura; se estiver certo, pode lançar.
            </span>
          ) : null}
          {faixa === null && tipo !== null ? (
            <span className="txt-apoio text-tinta-secundaria">Sem faixa cadastrada para {tipo}: o sistema não avisa se está fora do esperado.</span>
          ) : null}
        </>
      )}
    </div>
  );
}

function explicarFaltaDeConsumo(
  codigo: string,
  anterior: Abastecimento | null,
  litros: number | null,
  horas: number | null,
  unidade: string,
): string {
  if (!codigo) return 'Escolha o equipamento para o sistema calcular o consumo.';
  if (anterior === null) {
    return `Primeiro abastecimento de ${codigo} no sistema. O consumo aparece a partir do segundo.`;
  }
  if (anterior.horimetro === null) {
    return `O abastecimento anterior de ${codigo} entrou sem leitura, então não há de onde tirar a diferença. O consumo volta no próximo.`;
  }
  if (horas === null) {
    return `A leitura precisa ser maior que a do abastecimento anterior (${formatarAteDuasCasas(anterior.horimetro)} ${unidade}, em ${formatarDataHora(anterior.quando)}) para o consumo ser calculado.`;
  }
  if (litros === null || litros <= 0) return 'Informe os litros para ver o consumo.';
  return 'Informe os litros e a leitura do medidor para ver o consumo.';
}
